/**
 * Created by jonas on 2021-02-09.
 *
 * Super basic Symbol implementation that extends PIXI.Sprite
 *
 */

import {Karen} from "../Karen";
import {KarenSymbolState} from "./KarenSymbolConfig";
import {IParsedStackedSymbolName, StackedSymbol} from "../../core/reel/stackedsymbol/StackedSymbol";
import {IBaseSymbol} from "../../core/symbol/BaseSymbol";
import {MultiSprites} from "@nolimitcity/slot-game/bin/animation/MultiSprites";
import {gsap, Linear, Power4, TimelineLite} from "gsap";
import {KarenLayers, Layers} from "../view/KarenLayers";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {KarenMultiplier} from "../view/KarenMultiplier";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {SpinePlayer} from "../../core/displayObject/SpinePlayer";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/component/BitmapCountUp";
import {KarenGameAssets} from "../KarenGameAssets";
import {Translator} from "../../core/config/translate/Translator";
import {TranslationTexts} from "../../core/config/translate/TranslationTexts";
import {KarenTextStyles} from "../KarenTextStyles";
import {FontStyle, FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";

export class KarenSymbol extends PIXI.Container implements IBaseSymbol {
    public symbolWidth: number = 120;
    public symbolHeight: number = 84;
    public bottomPadding: number = 0;
    public symbolName: string;
    public stackedSymbolName: IParsedStackedSymbolName;
    public reelId: number;
    protected _animation: MultiSprites;
    protected _centerPosition: PIXI.Point;
    private _currentState: KarenSymbolState;
    private _multContainer: KarenMultiplier;
    private _multPrefix: string = "multFont";
    private _winTl: TimelineLite | null;
    private _winEffects: SpinePlayer;
    private _individualWinWaysAmountTL: gsap.core.Tween[] = [];

    constructor(symbolName: string, reelId: number, initState: KarenSymbolState) {
        super();
        this.symbolName = symbolName;
        this.stackedSymbolName = StackedSymbol.parseStackedSymbolName(symbolName, reelId);
        this.reelId = reelId;
        this.initAnimation();
        this.getStateAnimation(initState, {duration: 0, alpha: 1}, {duration: 0});
        if (this.reelId === 0) {
            this.symbolHeight = 210;
        }
        this._currentState = initState;
        this._multContainer = new KarenMultiplier(this.reelId);
    }

    public showWaysAndAmount(winAmount: number, comb: number, durr: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                this._individualWinWaysAmountTL = [];
                this.removeChild(winAmountCountUp);
                this.removeChild(winWays);
                winAmountCountUp.destroy();
                winWays.destroy();
            }
        });
        const winAmountCountUp: BitmapCountUp = this.createCountUp(winAmount),
            winWays: PIXI.Text = this.createWinWays(comb);
        this.addChild(winAmountCountUp, winWays);
        const countUpMoveTL: gsap.core.Tween = gsap.to(winAmountCountUp, {
                y: winAmountCountUp.y - 20,
                duration: durr
            }).pause(),
            winWaysMoveTL: gsap.core.Tween = gsap.to(winWays, {
                y: winWays.y - 20,
                duration: durr
            }).pause();
        this._individualWinWaysAmountTL.push(countUpMoveTL, winWaysMoveTL);
        tl.add(() => {
            winAmountCountUp.visible = true;
            winWays.visible = true;
        })
        tl.add([countUpMoveTL.play(), winWaysMoveTL.play()]);
        return tl;
    }

    public getStateAnimation(state: KarenSymbolState, inVars: gsap.TweenVars, outVars: gsap.TweenVars, frames?: number[]): TimelineLite {
        // TODO: verify the state from symbol config?
        // TODO: change the animation to spine if the symbol is using spine instead of sprite.
        this._currentState = state;
        this.createState(state);
        return this._animation.playSprite(state, inVars, outVars, frames);
    }

    public onRemove(): void {
        this.stopWin();
        this.destroy({children: true});
    }

    public getGlobalPosition(): PIXI.Point {
        if (this.parent) {
            return new PIXI.Point(this.position.x + this.parent.x,
                this.position.y + this.parent.y);
        }
        return this.position;
    }

    public revealMultiplier(multiplierVal: number, isInfected: boolean = false, noWin: boolean = false, overLap: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._multContainer.setMultiplierPosition((this.parent.x - 15), (this.parent.y + this.y + 60));
        this._multContainer.updateSpriteTexture(multiplierVal, isInfected);
        tl.to(this._multContainer, {alpha: 1, duration: 0.03, ease: Linear.easeIn}, 0);
        tl.add(this._multContainer.updateMValue(multiplierVal, this.symbolName, noWin, overLap), 0.01);
        return tl;
    }

    public revealQuickMultiplier(multiplierVal: number, noWin: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._multContainer.setMultiplierPosition((this.parent.x - 15), (this.parent.y + this.y + 60));
        this._multContainer.updateSpriteTexture(multiplierVal, false);
        tl.to(this._multContainer, {alpha: 1, duration: 0.03, ease: Linear.easeIn}, 0);
        tl.add(this._multContainer.updateMQuickerValue(multiplierVal, this.symbolName, noWin), 0.01);
        return tl;
    }

    public showXMultiplier(multiplierVal: number, isInfectious: boolean = false): void {
        KarenLayers.getLayer(Layers.SYMBOL_MULT)!.addChild(this._multContainer);
        this._multContainer.setMultiplierPosition((this.parent.x - 15), (this.parent.y + this.y + 60));
        this._multContainer.updateSpriteTexture(multiplierVal, isInfectious);
        if (multiplierVal > 9) {
            this._multContainer.x -= 7;
        }
        this._multContainer.alpha = 1;
        this._multContainer.scale.set(1);
        let multFontParent: PIXI.Text = this._multContainer.getChildByName(this._multPrefix + "_" + this.reelId) as PIXI.Text;
        multFontParent.text = "x" + multiplierVal;
        multFontParent.x = 28;
        this._multContainer.pivot.set(-28, 51);
    }

    public revealXWMultiplier(multiplierVal: number, isInfected: boolean = false, noWin: boolean = false, overLap: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._multContainer.updateSpriteTexture(multiplierVal);
        tl.add(this._multContainer.updateMValue(multiplierVal, this.symbolName, noWin, overLap), 0);
        this._multContainer.pivot.x = this._multContainer.width / 2;
        this._multContainer.pivot.y = this._multContainer.height / 2;
        this._multContainer.setMultiplierPosition((this.parent.x + 35), (this.parent.y + this.y + 28));
        return tl;
    }

    public getMutliper(): KarenMultiplier {
        return this._multContainer;
    }

    public getShakeAnimation(duration: number = 0.5): gsap.core.Timeline {
        const currentSprite: TimelineSprite = this._animation.sprites["normal"];
        const tl: TimelineLite = new TimelineLite();
        const x = Math.random() > 0.5 ? "-=2" : "+=2";
        const y = Math.random() > 0.5 ? "-=1" : "+=1";
        const rotPostFix: string = "0.03",
            rot = Math.random() > 0.5 ? "-=" + rotPostFix : "+=" + rotPostFix;
        tl.add(gsap.to(currentSprite, {
            duration: duration,
            x: x,
            y: y,
            rotation: rot,
            ease: Karen.xWShakeWiggle
        }));
        return tl;
    }

    public setInteractive(val: boolean): void {
        if (this.symbolName === "W") {
            return
        }
        const currentSprite: TimelineSprite = this._animation.sprites[this._currentState];
        currentSprite.interactive = val;
        currentSprite.on('pointerdown', () => {
            Karen.miniPaytable.onClicked({
                dispObj: currentSprite,
                name: this.name,
            });
        });
    }

    public playWin(winTypeName: string, toFade: boolean, delay = 0): TimelineLite {
        if (this._winTl && this._winTl.isActive()) {
            debugger;
            this._winTl.pause();
            this._winTl.kill();
        }
        let spineResourceName: string = this.getSpineAsset();
        const stagePosition = this.getStagePosition();
        if (!this._winEffects) {
            this._winEffects = new SpinePlayer(spineResourceName);
            this._winEffects.position.set(stagePosition.x, stagePosition.y);
            this._winEffects.visible = false;
            KarenLayers.getLayer(Layers.HIGH_SYMBOLS_WIN)!.addChild(this._winEffects);
        }
        const winTl: TimelineLite = new TimelineLite();
        winTl.add(() => {
            this._winEffects!.visible = true;
            this._winEffects!.alpha = 1;
        }, delay);
        const aniName = winTypeName;
        if (spineResourceName === "win_overlays_mx" && this.symbolName.indexOf("W") == -1) {
            winTl.add(this._winEffects.play({
                skinType: this.symbolName.toLowerCase(),
                name: aniName,
                track: 0,
                loop: false,
                showOnEnd: true
            }))
        } else {
            winTl.add(this._winEffects.play({
                name: aniName,
                track: 0,
                loop: false,
                showOnEnd: true,
            }));
        }
        let duration: number = this._winEffects!.fetchDuration(aniName);
        if (toFade) {
            winTl.add(this.playFadeOutAfterPause(), duration + 0.1);
        }
        this._winTl = winTl;
        return winTl;
    }

    public stopWin(): void {
        if (this._winTl) {
            this._winTl.pause();
            this._winTl.kill();
        }
        if (this._winEffects) {
            if (this._winEffects.parent) {
                this._winEffects.children.length = 0;
                this._winEffects.parent.removeChild(this._winEffects);
            }

        }
    }

    public getStagePosition(): PIXI.Point {
        if (this.parent) {
            return new PIXI.Point(this.position.x + this.parent.x,
                this.position.y + this.parent.y);
        }
        return this.position;
    }

    protected initAnimation(): void {
        this._centerPosition = this.getCenterPosition();
        this._animation = new MultiSprites();
        this._animation.position.set(this._centerPosition.x, this._centerPosition.y);
        this.addChild(this._animation);
    }

    protected getCenterPosition(): PIXI.Point {
        return new PIXI.Point(0, 0);
    }

    protected createState(state: KarenSymbolState): void {
        if (!this._animation.sprites[state]) {
            this._animation.addSprites({
                name: state,
                textures: this.getStateTexture(this.symbolName, state),
                anchor: new PIXI.Point(0.5, 0.5)
            });
        }
        if (state === KarenSymbolState.NO_WIN) {
            this._animation.sprites[state].tint = 0x555555;
            this.dimAllMultiplierChildren();
        }
    }

    protected getStateTexture(symName: string, state?: KarenSymbolState): PIXI.Texture[] {
        let texturePath: string = `${symName}/${symName}`;
        if (this.reelId === 0 && state !== KarenSymbolState.SPIN) {
            texturePath = `${symName}/${symName + "_0"}/${symName + "_0"}`;
        } else if (this.reelId === 0 && state === KarenSymbolState.SPIN) {
            texturePath = `${symName}/${symName + "_0"}/${symName + "_0"}` + `_${state}`;
        } else if (state === KarenSymbolState.SPIN) {
            texturePath += `_${state}`;
        }
        if (state === KarenSymbolState.NO_WIN && symName.indexOf("M") > -1) {
            texturePath += `_noWin`;
            if (this.reelId == 0) {
                texturePath = `${symName}/${symName + "_0"}/${symName + "_0"}` + `_${state}`;
            }
        }
        if (symName === "W" && ((state === KarenSymbolState.END) || (state === KarenSymbolState.NO_WIN))) {
            texturePath = `${symName}/${symName}` + `_` + state;
        }
        if (symName === "W" && ((state === KarenSymbolState.NO_WIN_END))) {
            texturePath = `${symName}/${symName}` + `_` + KarenSymbolState.NO_WIN;
        }
        return Karen.assetLoader.getTextures(texturePath);
    }

    private createCountUp(winAmount: number): BitmapCountUp {
        const countUp: BitmapCountUp = new BitmapCountUp({
            fontName: KarenGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 20,
            tint: 0xffffff,
        });
        countUp.visible = false;
        countUp.position.y = -10;
        countUp.getCountUpAnimation(winAmount, winAmount, 0.002);
        if (countUp.width >= this.symbolWidth - 20) {
            let ftSize: number = countUp.fontSize;
            while (countUp.width >= this.symbolWidth - 20) {
                countUp.fontSize = ftSize;
                --ftSize;
            }
        }
        return countUp;
    }

    private createWaysStyle(fontSize: number) {
        return {
            fontFamily: "Helvetica",
            fontStyle: FontStyle.ITALIC,
            fontSize: fontSize,
            fontWeight: FontWeight.EXTRA_BOLD,
            dropShadow: true,
            dropShadowAngle: 8,
            dropShadowBlur: 8,
            dropShadowDistance: 8,
            fill: "#FFFFFF",
            lineJoin: "round",
            align: "center",
            stroke: "#FF2600",
            strokeThickness: 8
        }
    }

    private createWinWays(comb: number): PIXI.Text {
        const winWays: PIXI.Text = new PIXI.Text(comb + " " + SlotGame.apiPlugin.translations.translate(comb > 1 ? "Ways" : "Way"), this.createWaysStyle(15));
        winWays.anchor.set(0.5);
        winWays.visible = false;
        winWays.position.y = 25;
        if (winWays.width >= this.symbolWidth - 20) {
            let ftSize: number = 15;
            while (winWays.width >= this.symbolWidth - 20) {
                --ftSize;
                winWays.style = this.createWaysStyle(ftSize);
            }
        }
        return winWays;
    }

    private makeStyle(fontSize: number) {
        return {
            fontFamily: "Helvetica",
            fontStyle: FontStyle.ITALIC,
            fontSize: fontSize,
            fontWeight: FontWeight.BOLD,
            dropShadow: true,
            dropShadowAngle: 2,
            dropShadowBlur: 2,
            dropShadowDistance: 2,
            fill: "#FFFFFF",
            lineJoin: "round",
            align: "center",
            stroke: "#FF2600",
            strokeThickness: 2
        }
    }

    private createWinWaysForWinHigh(ways: number): PIXI.Text {
        const winWays: PIXI.Text = new PIXI.Text(ways + " " + Translator.translate((ways === 1 ? TranslationTexts.WAY_WON : TranslationTexts.WAYS_WON)), this.makeStyle(30));
        if (winWays.width > this.symbolWidth - 20) {
            let fontSize: number = 35;
            while (winWays.width > this.symbolWidth - 20) {
                --fontSize;
                winWays.style = this.makeStyle(fontSize);
            }
        }
        winWays.anchor.set(0.5);
        winWays.position.y = 25;
        winWays.visible = false;
        return winWays;
    }

    private createWinAmountForWinHigh(winAmount: number): BitmapCountUp {
        const countUp: BitmapCountUp = new BitmapCountUp({
            fontName: KarenGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 35,
            tint: parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
        countUp.getCountUpAnimation(winAmount, winAmount, 0.002).progress(1);
        countUp.position.y = -10;
        if (countUp.width > this.symbolWidth - 20) {
            let fontSize: number = countUp.fontSize;
            while (countUp.width > this.symbolWidth - 20) {
                countUp.fontSize = fontSize;
                --fontSize;
            }
        }
        countUp.pivot.set(countUp.width / 2, countUp.height / 2);
        countUp.visible = false;
        return countUp;
    }

    private playFadeOutAfterPause(): TimelineLite {
        const fadeTl: TimelineLite = new TimelineLite();
        fadeTl.to(this._winEffects, 0.3, {alpha: 0, ease: Power4.easeOut});
        return fadeTl;
    }

    private dimAllMultiplierChildren(): void {
        this._multContainer.children.forEach((child: any) => {
            if (child) {
                child.tint = 0x555555;
            }
            if (child && child.name === "childContainer") {
                child.children.forEach((nestedChild: any) => {
                    if (nestedChild) {
                        nestedChild.tint = 0x555555;
                    }
                });
            }
        })
    }

    private getSpineAsset(): string {
        let spineResourceName: string = "win_overlays_" + this.symbolName.toLowerCase();
        if (this.symbolName.indexOf("L") > -1) {
            spineResourceName = "win_overlays_lx_" + this.symbolHeight;
        } else if (this.symbolName.indexOf("M") > -1 && this.symbolHeight === 210) {
            spineResourceName = "win_overlays_" + this.symbolName.toLowerCase();
        } else {
            spineResourceName = "win_overlays_mx";
        }
        return spineResourceName;
    }

    get individualWinWaysAmountTL(): gsap.core.Tween[] {
        return this._individualWinWaysAmountTL;
    }

    public get symbolStep(): number {
        return this.symbolHeight + this.bottomPadding;
    }

}

